import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { audiosService, coursesService } from '../services/api';
import { Audio, Course, AudioStreamInfo } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUtils';
import { getCoursePathSegment } from '../utils/coursePath';
import LoadingSpinner from '../components/LoadingSpinner';
import CourseAudioPlayer from '../components/audio/CourseAudioPlayer';
import { buildCourseAudioPlaybackUrl } from '../utils/audioPlaybackUrl';
import { formatMediaTime } from '../hooks/useHtmlAudioPlayer';
import {
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Headphones,
  LayoutDashboard,
  ListMusic,
} from 'lucide-react';

const toFaDigits = (value: string | number) =>
  String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const AudioPlayer: React.FC = () => {
  const { audioId } = useParams<{ audioId: string }>();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [audioInfo, setAudioInfo] = useState<AudioStreamInfo | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [courseAudios, setCourseAudios] = useState<Audio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [autoPlayNext, setAutoPlayNext] = useState(false);

  const loadProgress = useCallback((id: string): number => {
    try {
      const saved = localStorage.getItem(`audio_progress_${id}`);
      return saved ? parseFloat(saved) : 0;
    } catch {
      return 0;
    }
  }, []);

  const saveProgress = useCallback((id: string, time: number) => {
    try {
      localStorage.setItem(`audio_progress_${id}`, time.toString());
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (audioId) {
      void fetchAudioData();
    }
  }, [audioId, user, authLoading, navigate]);

  const fetchAudioData = async () => {
    try {
      setLoading(true);
      setError('');

      const streamData = await audiosService.getAudioStreamUrl(audioId!);
      setAudioInfo(streamData);
      setAudioUrl(buildCourseAudioPlaybackUrl(audioId!, streamData));

      if (streamData.courseId) {
        try { localStorage.setItem(`last_audio_${streamData.courseId}`, audioId!); } catch { /* ignore */ }

        const [courseData, audiosData] = await Promise.all([
          coursesService.getById(streamData.courseId),
          audiosService.getMyAudios(),
        ]);

        setCourse(courseData);
        const courseAudiosList = audiosData
          .filter((a) => a.courseId === streamData.courseId)
          .sort((a, b) => a.order - b.order);
        setCourseAudios(courseAudiosList);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'خطا در بارگذاری فایل صوتی');
    } finally {
      setLoading(false);
    }
  };

  /**
   * `autoPlay` فقط برای قطعه‌ای روشن می‌شود که خودکار بعد از پایان قبلی آمده.
   * اگر کاربر خودش فایلی را از لیست انتخاب کند، پخش‌کننده منتظر دکمهٔ پخش می‌ماند.
   */
  const handleAudioSelect = (selectedAudioId: string, autoPlay = false) => {
    setAutoPlayNext(autoPlay);
    const segment = course ? getCoursePathSegment(course) : courseId;
    navigate(`/courses/${segment}/audios/${selectedAudioId}`);
  };

  /** پایان یک فایل: اگر بعدی هست، برو و همان‌جا شروع کن */
  const handleTrackEnded = () => {
    if (hasNext) handleAudioSelect(courseAudios[currentIndex + 1].id, true);
  };

  const currentIndex = courseAudios.findIndex((a) => a.id === audioId);
  const hasNext = currentIndex >= 0 && currentIndex < courseAudios.length - 1;
  const hasPrevious = currentIndex > 0;

  const goToPrevious = () => {
    if (hasPrevious) handleAudioSelect(courseAudios[currentIndex - 1].id);
  };

  const goToNext = () => {
    if (hasNext) handleAudioSelect(courseAudios[currentIndex + 1].id);
  };

  type LessonMeta = { lessonTitle: string; lessonCaption?: string | null; chapterTitle: string; chapterDescription?: string | null };

  const audioLessonMeta = useMemo(() => {
    const map = new Map<string, LessonMeta>();
    if (!course?.chapters?.length) return map;
    for (const ch of course.chapters) {
      for (const lesson of ch.lessons) {
        if (lesson.audio?.id) {
          map.set(lesson.audio.id, {
            lessonTitle: lesson.title,
            lessonCaption: lesson.caption,
            chapterTitle: ch.title,
            chapterDescription: ch.description,
          });
        }
      }
    }
    return map;
  }, [course]);

  const chapteredAudios = useMemo(() => {
    if (!course?.chapters?.length) return null;

    const groups: { chapterTitle: string; chapterDescription?: string | null; audios: Audio[] }[] = [];
    const linkedAudioIds = new Set<string>();

    for (const ch of course.chapters) {
      const chapterAudios: Audio[] = [];
      for (const lesson of ch.lessons) {
        const audioId = lesson.audio?.id;
        if (!audioId) continue;
        const audio = courseAudios.find((a) => a.id === audioId);
        if (audio) {
          chapterAudios.push(audio);
          linkedAudioIds.add(audioId);
        }
      }
      if (chapterAudios.length > 0) {
        groups.push({
          chapterTitle: ch.title,
          chapterDescription: ch.description,
          audios: chapterAudios,
        });
      }
    }

    const orphanAudios = courseAudios.filter((a) => !linkedAudioIds.has(a.id));
    if (orphanAudios.length > 0) {
      groups.push({ chapterTitle: 'سایر فایل‌ها', audios: orphanAudios });
    }

    return groups.length > 0 ? groups : null;
  }, [course, courseAudios]);

  const pageShell = 'relative min-h-[100dvh] min-h-screen bg-[#E9EDF7] pb-[max(5.5rem,env(safe-area-inset-bottom,0px))] pt-3 text-[#0F172A] sm:pb-24 sm:pt-8';

  if (loading) {
    return (
      <div className={`${pageShell} flex items-center justify-center`} dir="rtl">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageShell} dir="rtl">
        <div className="mx-auto flex min-h-[60vh] max-w-lg items-center justify-center px-4">
          <div className="w-full overflow-hidden rounded-[1.25rem] border border-red-200/90 bg-white p-6 text-center shadow-[0_16px_40px_-24px_rgba(220,38,38,0.35)]">
            <h2 className="text-lg font-bold text-slate-900">خطا در دسترسی به فایل صوتی</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-2xl bg-[#1C3B9B] px-6 text-sm font-bold text-white transition hover:bg-[#153088] active:scale-[0.98]"
            >
              بازگشت به داشبورد
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!audioInfo || !audioId) {
    return (
      <div className={pageShell} dir="rtl">
        <div className="mx-auto flex min-h-[60vh] max-w-lg items-center justify-center px-4">
          <div className="w-full overflow-hidden rounded-[1.25rem] border border-slate-200/90 bg-white p-6 text-center shadow-[0_16px_40px_-24px_rgba(15,23,42,0.12)]">
            <h2 className="text-lg font-bold text-slate-900">فایل صوتی یافت نشد</h2>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-2xl bg-[#1C3B9B] px-6 text-sm font-bold text-white transition hover:bg-[#153088] active:scale-[0.98]"
            >
              بازگشت به داشبورد
            </button>
          </div>
        </div>
      </div>
    );
  }

  const instructorAvatarUrl = '/assets/faraz.jpg';
  const currentLesson = audioId ? audioLessonMeta.get(audioId) : undefined;
  const displayTitle = currentLesson?.lessonTitle ?? audioInfo.title;
  const displayCaption = currentLesson?.lessonCaption ?? audioInfo.description;
  const courseHref = course ? `/courses/${getCoursePathSegment(course)}` : '/courses';
  const courseCoverUrl =
    course?.thumbnail != null && String(course.thumbnail).trim()
      ? getImageUrl(course.thumbnail) ?? null
      : null;

  return (
    <div className={pageShell} dir="rtl">
      <style>{`
        .fa-list-eq { height: 14px; transform-origin: bottom; animation: fa-list-eq-kf 0.85s ease-in-out infinite; }
        @keyframes fa-list-eq-kf { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
        .fa-audio-scroll { scrollbar-width: thin; scrollbar-color: #c3cbe0 transparent; }
        .fa-audio-scroll::-webkit-scrollbar { width: 6px; }
        .fa-audio-scroll::-webkit-scrollbar-track { background: transparent; margin: 6px 0; }
        .fa-audio-scroll::-webkit-scrollbar-thumb { background-color: #c3cbe0; border-radius: 9999px; }
        .fa-audio-scroll::-webkit-scrollbar-thumb:hover { background-color: #9aa6c4; }
      `}</style>
      <div className="relative mx-auto max-w-7xl px-[max(0.75rem,env(safe-area-inset-right,0px),env(safe-area-inset-left,0px))] sm:px-6 lg:px-8">
        <nav
          aria-label="مسیر صفحه"
          className="mb-3 flex snap-x snap-mandatory flex-nowrap items-center gap-x-1.5 overflow-x-auto pb-0.5 text-[10px] text-[#64748B] [-ms-overflow-style:none] [scrollbar-width:none] sm:mb-4 sm:flex-wrap sm:gap-2 sm:text-sm [&::-webkit-scrollbar]:hidden"
        >
          <span
            role="presentation"
            className="snap-start shrink-0 cursor-pointer transition hover:text-[#1C3B9B]"
            onClick={() => navigate('/')}
          >
            خانه
          </span>
          <span className="shrink-0 text-slate-300">/</span>
          <span
            role="presentation"
            className="snap-start shrink-0 cursor-pointer transition hover:text-[#1C3B9B]"
            onClick={() => navigate('/courses')}
          >
            دوره‌ها
          </span>
          {course ? (
            <>
              <span className="shrink-0 text-slate-300">/</span>
              <span
                role="presentation"
                className="max-w-[45vw] cursor-pointer truncate font-semibold text-[#0F172A] transition hover:text-[#1C3B9B] sm:max-w-none"
                onClick={() => navigate(courseHref)}
                title={course.title}
              >
                {course.title}
              </span>
            </>
          ) : null}
          <span className="shrink-0 text-slate-300">/</span>
          <span className="snap-start shrink-0 font-semibold text-[#0F172A]">پخش صوتی</span>
        </nav>

        <div className="-mx-3 mb-3 w-[calc(100%+1.5rem)] rounded-[1.25rem] border border-slate-300/60 bg-transparent px-2.5 py-2.5 sm:mx-0 sm:mb-6 sm:w-auto sm:rounded-[1.5rem] sm:px-5 sm:py-5 md:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              {courseCoverUrl ? (
                <div className="relative shrink-0 overflow-hidden rounded-2xl ring-1 ring-slate-300/70 sm:rounded-[1.125rem]">
                  <img
                    src={courseCoverUrl}
                    alt={course ? `کاور دوره ${course.title}` : 'کاور دوره'}
                    className="h-[4.25rem] w-[4.25rem] object-cover sm:h-[5.75rem] sm:w-[5.75rem]"
                    loading="lazy"
                  />
                </div>
              ) : null}
              <div className="min-w-0 flex-1 text-right">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1C3B9B]/10 px-2.5 py-1 text-[9px] font-bold tracking-wide text-[#1C3B9B] ring-1 ring-inset ring-[#1C3B9B]/20 sm:text-[11px]">
                  <Headphones className="size-3 sm:size-3.5" strokeWidth={2.5} aria-hidden />
                  پخش صوتی دوره
                </span>
                <h1 className="mt-1.5 text-[1.05rem] font-black leading-snug tracking-tight text-slate-900 sm:text-2xl md:text-[1.7rem]">
                  {displayTitle}
                </h1>
                {course ? (
                  <p className="mt-0.5 truncate text-[11px] text-slate-600 sm:mt-1 sm:text-sm">
                    {course.title}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end sm:gap-2.5">
              <button
                type="button"
                onClick={() => navigate(courseHref)}
                className="group inline-flex min-h-[42px] items-center justify-center gap-2 rounded-2xl bg-[#1C3B9B] px-3.5 py-2.5 text-[11px] font-black tracking-tight text-white transition hover:bg-[#173183] active:scale-[0.98] sm:min-h-[48px] sm:gap-2.5 sm:px-6 sm:text-sm"
              >
                <BookOpen className="size-4 shrink-0 text-white transition-transform group-hover:-translate-x-0.5 sm:size-[1.15rem]" strokeWidth={2.5} aria-hidden />
                <span className="leading-snug">بازگشت به دوره</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-transparent px-3.5 py-2.5 text-[11px] font-black tracking-tight text-slate-700 transition hover:border-[#1C3B9B]/50 hover:text-[#1C3B9B] active:scale-[0.98] sm:min-h-[48px] sm:gap-2.5 sm:px-6 sm:text-sm [&_svg]:text-slate-500 hover:[&_svg]:text-[#1C3B9B]"
              >
                <LayoutDashboard className="size-4 shrink-0 sm:size-[1.15rem]" strokeWidth={2.5} aria-hidden />
                <span className="leading-snug">داشبورد</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-[minmax(0,1fr)_24rem] lg:gap-8">
          <div className="min-w-0 w-full max-w-full space-y-4 sm:space-y-5">
            <CourseAudioPlayer
              src={audioUrl}
              mediaKey={audioId}
              title={displayTitle}
              subtitle={course?.title || 'فایل صوتی دوره'}
              coverUrl={instructorAvatarUrl}
              knownDurationSeconds={audioInfo.duration}
              variant="full"
              showVolume
              jumpSeconds={30}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              onPrevious={goToPrevious}
              onNext={goToNext}
              autoPlay={autoPlayNext}
              onEnded={handleTrackEnded}
              loadSavedProgress={() => loadProgress(audioId)}
              onProgressSave={(time) => saveProgress(audioId, time)}
              onError={setError}
            />

            <div className="overflow-hidden rounded-[1.25rem] border border-slate-300/60 bg-transparent px-3 py-4 sm:px-6 sm:py-6">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="h-4 w-1 rounded-full bg-[#1C3B9B]" aria-hidden />
                <span className="text-[10px] font-bold tracking-wide text-[#1C3B9B] sm:text-[11px]">درباره این بخش</span>
              </div>
              <h2 className="text-[0.9375rem] font-black leading-snug tracking-tight text-slate-900 sm:text-xl md:text-2xl">
                {displayTitle}
              </h2>
              {displayCaption ? (
                <p className="mt-2 text-[13px] leading-relaxed text-[#475569] sm:text-sm">
                  {displayCaption}
                </p>
              ) : (
                <p className="mt-2 text-[13px] leading-relaxed text-slate-400 sm:text-sm">
                  توضیحات تکمیلی برای این فایل صوتی ثبت نشده است.
                </p>
              )}

              {courseAudios.length > 1 ? (
                <div className="mt-5 flex items-center justify-center gap-2 border-t border-slate-200/70 pt-4 sm:gap-3 sm:pt-5">
                  <button
                    type="button"
                    onClick={goToNext}
                    disabled={!hasNext}
                    className={`inline-flex min-h-[40px] items-center justify-center gap-1 rounded-full px-5 py-2 text-[13px] font-bold transition sm:gap-1.5 sm:text-sm ${
                      hasNext
                        ? 'bg-[#1C3B9B] text-white hover:bg-[#173183] active:scale-[0.98]'
                        : 'cursor-not-allowed bg-slate-100 text-slate-400'
                    }`}
                  >
                    <ChevronRight className="size-4 shrink-0" strokeWidth={2.5} aria-hidden />
                    بعدی
                  </button>

                  <span className="min-w-[4.25rem] shrink-0 rounded-full border border-slate-300/70 bg-white px-3 py-1.5 text-center text-[11px] font-bold tabular-nums text-slate-600">
                    {toFaDigits(currentIndex + 1)} از {toFaDigits(Math.max(courseAudios.length, 1))}
                  </span>

                  <button
                    type="button"
                    onClick={goToPrevious}
                    disabled={!hasPrevious}
                    className={`inline-flex min-h-[40px] items-center justify-center gap-1 rounded-full px-4 py-2 text-[13px] font-bold transition sm:gap-1.5 sm:text-sm ${
                      hasPrevious
                        ? 'border border-slate-300 bg-white text-slate-700 hover:border-[#1C3B9B]/50 hover:text-[#1C3B9B] active:scale-[0.98]'
                        : 'cursor-not-allowed border border-slate-200 bg-transparent text-slate-400'
                    }`}
                  >
                    قبلی
                    <ChevronLeft className="size-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <aside className="min-w-0">
            <div className="isolate z-40 overflow-hidden rounded-[1.25rem] border border-slate-300/60 bg-transparent sm:rounded-[1.375rem] lg:sticky lg:top-[calc(7.375rem+env(safe-area-inset-top,0px))]">
              <div className="border-b border-slate-300/50 bg-transparent px-3 py-2.5 sm:px-4 sm:py-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#1C3B9B] text-white sm:size-10">
                    <ListMusic className="size-4 sm:size-[1.125rem]" strokeWidth={2.25} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1 text-right">
                    <h3 className="text-[13px] font-black tracking-tight text-slate-900 sm:text-[0.95rem]">
                      فهرست فایل‌های صوتی
                    </h3>
                    <p className="mt-0.5 text-[9px] text-slate-500 sm:text-[10px]">
                      {toFaDigits(courseAudios.length)} فایل • برای تعویض انتخاب کنید
                    </p>
                  </div>
                </div>
              </div>
              <div className="fa-audio-scroll max-h-[min(40dvh,300px)] overflow-y-auto overscroll-contain px-1.5 py-2 sm:max-h-[min(70dvh,520px)] sm:p-3 md:p-4">
                {chapteredAudios ? (
                  <ChapteredAudioList
                    groups={chapteredAudios}
                    activeAudioId={audioId!}
                    onSelect={handleAudioSelect}
                    lessonMeta={audioLessonMeta}
                  />
                ) : (
                  <FlatAudioList
                    audios={courseAudios}
                    activeAudioId={audioId!}
                    onSelect={handleAudioSelect}
                    lessonMeta={audioLessonMeta}
                  />
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;

type AudioItemProps = {
  audio: Audio;
  index: number;
  isActive: boolean;
  onSelect: (id: string) => void;
  lessonMeta?: Map<string, { lessonTitle: string; lessonCaption?: string | null }>;
};

const AudioItem: React.FC<AudioItemProps> = ({ audio, index, isActive, onSelect, lessonMeta }) => {
  const meta = lessonMeta?.get(audio.id);
  const title = meta?.lessonTitle ?? audio.title;
  const caption = meta?.lessonCaption ?? audio.description;

  return (
  <button
    type="button"
    onClick={() => onSelect(audio.id)}
    aria-current={isActive ? 'true' : undefined}
    className={`group relative flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-right transition active:scale-[0.995] sm:px-2.5 sm:py-2 ${
      isActive
        ? 'bg-white shadow-[0_6px_18px_-12px_rgba(28,59,155,0.45)] ring-1 ring-[#1C3B9B]/12'
        : 'bg-transparent hover:bg-white/60'
    }`}
  >
    {isActive ? (
      <span aria-hidden className="absolute inset-y-2 right-0 w-[3px] rounded-full bg-[#1C3B9B]" />
    ) : null}
    {isActive ? (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#1C3B9B] text-white sm:size-9">
        <span className="flex h-3.5 items-end gap-[2px]" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="fa-list-eq w-[2px] rounded-full bg-white"
              style={{ animationDelay: `${i * 0.16}s` }}
            />
          ))}
        </span>
      </div>
    ) : (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums text-slate-400 transition group-hover:text-[#1C3B9B] sm:size-9">
        {toFaDigits(index + 1)}
      </div>
    )}
    <div className="min-w-0 flex-1">
      <p className={`line-clamp-2 text-[11px] font-bold leading-snug sm:text-[13px] ${isActive ? 'text-[#1C3B9B]' : 'text-slate-600 group-hover:text-slate-900'}`}>
        {title}
      </p>
      {caption ? (
        <p className="mt-0.5 line-clamp-1 text-[9px] text-slate-400 sm:text-[10px]">{caption}</p>
      ) : null}
    </div>
    {audio.duration ? (
      <span className={`shrink-0 font-mono text-[10px] tabular-nums sm:text-[11px] ${isActive ? 'text-[#1C3B9B]/80' : 'text-slate-400'}`}>
        {formatMediaTime(audio.duration)}
      </span>
    ) : null}
  </button>
  );
};

type FlatAudioListProps = {
  audios: Audio[];
  activeAudioId: string;
  onSelect: (id: string) => void;
  lessonMeta?: Map<string, { lessonTitle: string; lessonCaption?: string | null }>;
};

const FlatAudioList: React.FC<FlatAudioListProps> = ({ audios, activeAudioId, onSelect, lessonMeta }) => (
  <div className="space-y-0.5">
    {audios.map((audio, i) => (
      <AudioItem key={audio.id} audio={audio} index={i} isActive={audio.id === activeAudioId} onSelect={onSelect} lessonMeta={lessonMeta} />
    ))}
  </div>
);

type ChapterGroup = {
  chapterTitle: string;
  chapterDescription?: string | null;
  audios: Audio[];
};

type ChapteredAudioListProps = {
  groups: ChapterGroup[];
  activeAudioId: string;
  onSelect: (id: string) => void;
  lessonMeta?: Map<string, { lessonTitle: string; lessonCaption?: string | null }>;
};

const ChapteredAudioList: React.FC<ChapteredAudioListProps> = ({ groups, activeAudioId, onSelect, lessonMeta }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (title: string) =>
    setCollapsed((prev) => ({ ...prev, [title]: !prev[title] }));

  let globalIndex = 0;
  return (
    <div className="space-y-2">
      {groups.map((group) => {
        const isOpen = !collapsed[group.chapterTitle];
        const startIndex = globalIndex;
        globalIndex += group.audios.length;
        return (
          <div key={group.chapterTitle}>
            <button
              type="button"
              onClick={() => toggle(group.chapterTitle)}
              className="flex w-full items-center justify-between gap-2 rounded-lg bg-transparent px-1.5 py-1.5 text-right transition hover:bg-white/50"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="h-3 w-[3px] shrink-0 rounded-full bg-[#1C3B9B]" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-black text-slate-700 sm:text-xs">{group.chapterTitle}</p>
                  {group.chapterDescription ? (
                    <p className="mt-0.5 line-clamp-1 text-[9px] text-slate-400 sm:text-[10px]">{group.chapterDescription}</p>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="rounded-full bg-slate-200/60 px-1.5 py-0.5 text-[9px] font-bold text-slate-500">
                  {toFaDigits(group.audios.length)}
                </span>
                <ChevronDown
                  className={`size-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  strokeWidth={2.5}
                  aria-hidden
                />
              </div>
            </button>
            {isOpen ? (
              <div className="mt-1 space-y-0.5">
                {group.audios.map((audio, i) => (
                  <AudioItem
                    key={audio.id}
                    audio={audio}
                    index={startIndex + i}
                    isActive={audio.id === activeAudioId}
                    onSelect={onSelect}
                    lessonMeta={lessonMeta}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
